import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { MobileView } from "react-device-detect";
import { Trans } from "react-i18next";
import { AdvanceSearchSchema } from "../schemas/Patient.schema";
import "../styles/components/AdvanceSearch.scss";
import Datepicker from "./Datepicker";
import Gender from "./Gender";
import TextInput from "./TextInput";
import { useFormik } from "formik";
import moment from "moment";
import Utility from "../utility/Utility";
import ErrorBoundaryHandler from "./ErrorBoundaryHandler";
import { updateSearchStatus } from "../redux/reducers/PatientSlice";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import {
  PersistState
} from "../../src/redux/reducers/PersistSlice";

const AdvanceSearch = (props: any) => {
  const [patientDOB, setPatientDOB] = useState<string>("");
  const [patientIdType, setPatientIdType] = useState("");
  const [validateOnChangeFlag, setValidateOnChangeFlag] = useState(false);
  const [emptySearchError, setEmptySearchError] = useState(false);
  const [startDate, setStartDate] = useState<any>();
  const userState = Utility.getStateOfUser();
  const dispatch = useAppDispatch();
  const userPersistData = useAppSelector(PersistState);
  const userCountry = userPersistData.country;

  function scrollOnInputSelected() {
    if (
      document?.activeElement?.tagName == "INPUT" ||
      document?.activeElement?.tagName == "TEXTAREA" ||
      document?.activeElement?.tagName == "SELECT"
    ) {
      window.setTimeout(function () {
        (document?.activeElement as any)?.scrollIntoViewIfNeeded();
      }, 0);
    }
  }

  useEffect(() => {
    window.addEventListener("resize", scrollOnInputSelected);
    return function cleanupListener() {
      window.removeEventListener("resize", scrollOnInputSelected);
    };
  }, []);

  const formik = useFormik({
    initialValues: {
      patientName: "",
      birthDate: "",
      idInAdvSearch: "",
      phone: "",
    },
    validateOnChange: validateOnChangeFlag,
    onSubmit: () => {
      handleSearchClick();
    },
    validationSchema: AdvanceSearchSchema,
  });

  const handleIdTypeChange = (e: any) => {
    setPatientIdType(e.target.value);
  };

  const patientDOBWithoutError = () => {
    if (
      document.querySelector(".advancedSearch .c-els-field__message--error")
    ) {
      formik.setFieldValue("birthDate", "");
      setPatientDOB("");
      setStartDate(null);
      return null;
    } else {
      return formik.values["birthDate"];
    }
  };

  const handleSearchClick = () => {
    let selectedGender = "";
    if (document.querySelector('input[name="gender"]')) {
      selectedGender =
        document.querySelector('input[name="gender"]:checked')?.id || "";
    }
    const mobileNumber = (document.getElementById("phone") as HTMLInputElement)
      ?.value;

    const idNumber = (
      document.getElementById("idInAdvSearch") as HTMLInputElement
    )?.value?.toLowerCase();

    const searchPatient = {
      name: (document.getElementById("patientName") as HTMLInputElement)?.value,
      // birthdate:
      //   patientDOB !== "Invalid date" && patientDOB.length > 0
      //     ? patientDOB
      //     : "",
      birthdate: patientDOBWithoutError(),
      phone: mobileNumber,
      ...(idNumber && { identifier: idNumber }),
      gender: selectedGender,
    };

    const hasValue = Object.values(searchPatient).filter(val => val);

    if (Object.keys(hasValue).length === 0) {
      setEmptySearchError(true);
      return;
    } else {
      setEmptySearchError(false);
    }
    props.onSearchClick(searchPatient);
    dispatch(updateSearchStatus(true));
  };

  const handleDate = (e: any) => {
    setStartDate(e);
    formik.setFieldValue(
      "birthDate",
      moment(e, "DD/MM/YYYY").format("YYYY-MM-DD")
    );
    setPatientDOB(e);
    setEmptySearchError(false);
  };

  const handleDateFormat = (inputValue: string) => {
    return (inputValue = inputValue.replace(/(\d?\/\d?\/\d{4})/gi, ""));
  };

  return (
    <div className="advancedSearch">
      <div>
        <MobileView>
          <h5 className="heading">
            <Trans i18nKey={"advanced_search"}></Trans>
          </h5>
        </MobileView>
        <span className="description">
          <Trans i18nKey={"enter_details"}></Trans>
        </span>
        <br></br>
        {emptySearchError && (
          <div className="errorBox mt-3">
            <img src="/icons/err-alert.svg" style={{ marginRight: "13px" }} />
            <Trans i18nKey={"error.invalid_search"}></Trans>
          </div>
        )}
        <form
          className="p-0"
          onSubmit={e => {
            e.preventDefault();
            setValidateOnChangeFlag(true);
            formik.handleSubmit(e);
          }}
        >
          <div className="mt-3">
            <TextInput
              label="Patient Name"
              label_key="patient_name"
              type="text"
              id="patientName"
              onChange={formik.handleChange}
              error={formik.errors.patientName}
              maxLength={50}
            />
          </div>
          <>
            <div className="row mt-30">
              <Datepicker
                onChange={handleDate}
                testId={"dp-advance"}
                label_key="birth_date"
                minDt={new Date(new Date().getFullYear() - 100, 1, 1)} // passes 100 years less than the current year to the Datepicker component
                onInput={handleDateFormat}
              />
            </div>
          </>
          <div className="mt-30">
            <TextInput
              label="Mobile Number"
              label_key="mobile_number"
              type="number"
              id="phone"
              maxLength={10}
              error={formik.errors.phone}
              onChange={formik.handleChange}
            />
          </div>
          <div className="mt-30">
            <TextInput
              label="Beneficiary ID"
              label_key="id_number"
              type="idNumber"
              id="idInAdvSearch"
              error={formik.errors.idInAdvSearch}
              onChange={formik.handleChange}
              maxLength={14}
              dynamicPlaceHolder={Utility.getPlaceholderForState(userState, userCountry)}
            />
          </div>
          <div className="mt-30 ">
            <Gender onChange={() => { }} />
          </div>
          <div className="d-grid gap-3 mt-30">
            <Button
              className="searchBtn"
              data-testid="searchBtn"
              type="submit"
              size="lg"
            >
              <Trans i18nKey={"search"}></Trans>
            </Button>
            <MobileView className="d-grid gap-2">
              <Button
                data-testid="closeBtn"
                className={`closeBtn btn-outline`}
                onClick={props.onCloseClick}
                size="lg"
              >
                <Trans i18nKey={"close"}></Trans>
              </Button>
            </MobileView>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ErrorBoundaryHandler(AdvanceSearch);
